import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Format axios/API error into a friendly string.
 * Handles: Pydantic array-of-errors, plain string detail, unknown shapes.
 * Prevents "Objects are not valid as React child" crashes when toast.error() gets an array.
 */
/**
 * Översätt vanliga Pydantic/FastAPI-valideringsmeddelanden från engelska → svenska
 * så användare inte ser "Input should be a valid date or datetime".
 */
const FIELD_LABELS_SV = {
  start_date: 'Startdatum',
  end_date: 'Deadline',
  customer_email: 'Kundens e-post',
  customer: 'Kund',
  budget: 'Budget',
  location: 'Plats',
  name: 'Projektnamn',
  description: 'Beskrivning',
  billing_profile_id: 'Fakturabolag',
  project_hourly_rate: 'Projektets timtaxa',
  hourly_rate: 'Timtaxa',
  personnummer: 'Personnummer',
  phone: 'Telefon',
  email: 'E-post',
  org_number: 'Org.nummer',
  password: 'Lösenord',
};

function _translateValidationMsg(msg) {
  if (!msg || typeof msg !== 'string') return msg;
  const m = msg.toLowerCase();
  if (m.includes('valid date')) return 'måste vara ett giltigt datum (ÅÅÅÅ-MM-DD)';
  if (m.includes('valid email')) return 'måste vara en giltig e-postadress';
  if (m.includes('valid number') || m.includes('parse string as a number')) return 'måste vara ett giltigt nummer';
  if (m.includes('valid integer')) return 'måste vara ett heltal';
  if (m.includes('too short') || m.includes('string too short')) return 'får inte vara tom';
  if (m.includes('too long') || m.includes('string too long')) return 'är för lång';
  if (m.includes('field required')) return 'är obligatoriskt';
  if (m.includes('value error')) return 'har felaktigt värde';
  if (m.includes('not a valid')) return 'har felaktigt format';
  return msg;
}

function _formatPydanticItem(item) {
  if (typeof item === 'string') return item;
  // Pydantic item: { loc: ['body', 'start_date'], msg: 'Input should be a valid date', type: 'value_error.date' }
  const loc = Array.isArray(item?.loc) ? item.loc : [];
  const field = loc[loc.length - 1];
  const sv_field = field ? (FIELD_LABELS_SV[field] || field) : '';
  const sv_msg = _translateValidationMsg(item?.msg || item?.message || '');
  return sv_field ? `${sv_field} ${sv_msg}` : sv_msg;
}

/**
 * Converts FastAPI validation error arrays/objects into a user-friendly string.
 * Prevents "Objects are not valid as React child" crashes when toast.error() gets an array.
 */
export function formatApiError(err, fallback = 'Något gick fel') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return _translateValidationMsg(detail);
  if (Array.isArray(detail)) {
    // Deduplicera (Pydantic ger ofta samma fel flera gånger för union-types)
    const seen = new Set();
    const items = detail
      .map(_formatPydanticItem)
      .filter(s => { if (!s || seen.has(s)) return false; seen.add(s); return true; });
    return items.join(' · ');
  }
  if (detail && typeof detail === 'object') {
    return _formatPydanticItem(detail);
  }
  if (err?.message) return err.message;
  return fallback;
}

// Create axios instance with credentials for httpOnly cookie auth
const ax = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Fallback: also send Authorization header if token exists in localStorage (migration — will be removed)
ax.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const api = {
  // Projects
  getProjects: (params) => ax.get('/projects', { params }),
  createProject: (data) => ax.post('/projects', data),
  getProject: (id) => ax.get(`/projects/${id}`),
  updateProject: (id, data) => ax.put(`/projects/${id}`, data),
  deleteProject: (id) => ax.delete(`/projects/${id}`),
  setProjectGeofence: (id, data) => ax.put(`/projects/${id}/geofence`, data),
  removeProjectGeofence: (id) => ax.delete(`/projects/${id}/geofence`),
  getAllGeofences: () => ax.get('/projects/geofences/all'),
  getProjectCostSummary: (id) => ax.get(`/projects/${id}/cost-summary`),

  // Employees
  getEmployees: () => ax.get('/employees'),
  createEmployee: (data) => ax.post('/employees', data),
  updateEmployee: (id, data) => ax.put(`/employees/${id}`, data),
  deleteEmployee: (id) => ax.delete(`/employees/${id}`),
  setEmployeePin: (id, data) => ax.put(`/employees/${id}/pin`, data),

  // Machines
  getMachines: () => ax.get('/machines'),
  createMachine: (data) => ax.post('/machines', data),
  deleteMachine: (id) => ax.delete(`/machines/${id}`),

  // Time Reports
  getTimeReports: (params) => ax.get('/time-reports', { params }),
  createTimeReport: (data) => ax.post('/time-reports', data),
  updateTimeReportStatus: (id, status) => ax.put(`/time-reports/${id}/status?status=${status}`),
  updateTimeReport: (id, data) => ax.put(`/time-reports/${id}`, data),
  deleteTimeReport: (id) => ax.delete(`/time-reports/${id}`),
  listBillableTimeReports: (params) => ax.get('/invoicing/time-reports/billable', { params }),
  attachTimeReportsToInvoice: (invoiceId, timeReportIds) =>
    ax.post(`/invoicing/invoices/${invoiceId}/attach-time-reports`, { time_report_ids: timeReportIds }),
  listHandledClockEntries: (days = 30) => ax.get('/clock/handled', { params: { days } }),
  revertClockApproval: (id) => ax.post(`/clock/${id}/revert-approval`),
  // Projekt-offert (Quote)
  generateProjectQuote: (projectId) => ax.post(`/projects/${projectId}/generate-quote`),
  sendProjectQuote: (projectId, data) => ax.post(`/projects/${projectId}/send-quote`, data),
  startProject: (projectId, data) => ax.post(`/projects/${projectId}/start`, data || {}),

  // Machine Hours
  getMachineHours: (params) => ax.get('/machine-hours', { params }),
  createMachineHour: (data) => ax.post('/machine-hours', data),

  // ETA Reports
  getETAReports: (params) => ax.get('/eta-reports', { params }),
  getETAReport: (id) => ax.get(`/eta-reports/${id}`),
  createETAReport: (data) => ax.post('/eta-reports', data),
  updateETAStatus: (id, status) => ax.put(`/eta-reports/${id}/status?status=${status}`),
  getETAPhotos: (id) => ax.get(`/eta-reports/${id}/photos`),

  // Daybooks
  getDaybooks: (params) => ax.get('/daybooks', { params }),
  getDaybook: (id) => ax.get(`/daybooks/${id}`),
  createDaybook: (data) => ax.post('/daybooks', data),
  updateDaybook: (id, content) => ax.put(`/daybooks/${id}?content=${encodeURIComponent(content)}`),
  generateDaybook: (data) => ax.post('/daybooks/generate', data),

  // Invoice Drafts
  getInvoiceDrafts: (params) => ax.get('/invoice-drafts', { params }),
  createInvoiceDraft: (data) => ax.post('/invoice-drafts', data),
  updateInvoiceStatus: (id, status) => ax.put(`/invoice-drafts/${id}/status?status=${status}`),
  sendInvoice: (id) => ax.post(`/invoice-drafts/${id}/send`),
  sendInvoiceReminder: (id) => ax.post(`/invoice-drafts/${id}/send-reminder`),
  downloadInvoicePdf: (id) => ax.get(`/invoice-drafts/${id}/pdf`, { responseType: 'blob' }),
  downloadDaybookPdf: (id) => ax.get(`/daybooks/${id}/pdf`, { responseType: 'blob' }),
  downloadEtaPdf: (id) => ax.get(`/eta-reports/${id}/pdf`, { responseType: 'blob' }),
  downloadMaterialPlanPdf: (id) => ax.get(`/materials/material-plan/${id}/pdf`, { responseType: 'blob' }),

  // Tenders
  getTenders: () => ax.get('/tenders'),
  getTender: (id) => ax.get(`/tenders/${id}`),
  uploadTender: (formData) => ax.post('/tenders', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),

  // Reminders
  getReminders: (params) => ax.get('/reminders', { params }),
  createReminder: (data) => ax.post('/reminders', data),
  updateReminder: (id, data) => ax.put(`/reminders/${id}`, data),
  deleteReminder: (id) => ax.delete(`/reminders/${id}`),

  // Signatures
  createSignature: (data) => ax.post('/signatures', data),
  getSignatures: (params) => ax.get('/signatures', { params }),

  // Clock In/Out
  clockIn: (data) => ax.post('/clock/in', data),
  clockOut: (data) => ax.post('/clock/out', data),
  getClockStatus: () => ax.get('/clock/status'),
  getClockHistory: () => ax.get('/clock/history'),
  updateClockLocation: (data) => ax.post('/clock/location', data),

  // Live Activity
  getLiveActivity: () => ax.get('/activity/live'),

  // Customer Access
  createCustomerAccess: (data) => ax.post('/customer-access/create', data),
  getCustomerAccessList: (projectId) => ax.get(`/customer-access/project/${projectId}`),

  // Customer Portal (Public — uses own token)
  getCustomerProjectView: (token) => axios.get(`${API_URL}/customer/project/${token}`),
  customerLogin: (data) => axios.post(`${API_URL}/customer/login`, data),
  customerRegister: (data) => axios.post(`${API_URL}/customer/register`, data),
  customerGetProjects: (token) => axios.get(`${API_URL}/customer/portal/projects`, { headers: { Authorization: `Bearer ${token}` } }),
  customerGetProject: (id, token) => axios.get(`${API_URL}/customer/portal/project/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Dashboard
  getDashboardStats: () => ax.get('/dashboard/stats'),
  getWidgetPreferences: () => ax.get('/dashboard/widgets'),
  saveWidgetPreferences: (data) => ax.put('/dashboard/widgets', data),

  // Settings
  updateProfile: (data) => ax.put('/settings/profile', data),
  getCompany: () => ax.get('/settings/company'),
  updateCompany: (data) => ax.put('/settings/company', data),
  verifyCompany: (data) => ax.post('/settings/company/verify', data),
  changePassword: (data) => ax.put('/settings/password', data),
  getCompanyUsers: () => ax.get('/settings/users'),
  createCompanyUser: (data) => ax.post('/settings/users', data),
  deleteCompanyUser: (id) => ax.delete(`/settings/users/${id}`),
  setPinCode: (data) => ax.put('/settings/pin', data),
  getPinStatus: () => ax.get('/settings/pin'),
  verifyPin: (pin) => ax.post('/settings/verify-pin', { pin_code: pin }),

  // Billing
  getBillingPlans: () => ax.get('/billing/plans'),
  getCurrentSubscription: () => ax.get('/billing/subscription/current'),
  getVerificationStatus: () => ax.get('/company/verification-status'),
  verifyRegistrationPayment: (sessionId) => ax.get(`/auth/register/verify-payment?session_id=${sessionId}`),

  // Materials
  getMaterialPlans: (projectId) => ax.get(`/materials/material-plan/project/${projectId}`),
  getMaterialAIProfile: () => ax.get('/materials/material-ai-profile'),
  getMaterialPlan: (planId) => ax.get(`/materials/material-plan/${planId}`),
  generate3DModel: (planId) => ax.post(`/blueprint3d/plan/${planId}/generate`),
  get3DModel: (planId) => ax.get(`/blueprint3d/plan/${planId}`),
  listPlanFiles: (planId) => ax.get(`/blueprint3d/plan/${planId}/files`),
  getPlanFile: (planId, idx, preview = false) => ax.get(`/blueprint3d/plan/${planId}/files/${idx}`, { params: { preview }, responseType: 'blob' }),
  getProjectSummaries: () => ax.get('/blueprint3d/project-summaries'),
  updateMaterialPlan: (planId, data) => ax.put(`/materials/material-plan/${planId}`, data),
  deleteMaterialPlan: (planId) => ax.delete(`/materials/material-plan/${planId}`),
  generateMaterialPlan: (data) => ax.post('/materials/material-plan/generate', data, { timeout: 120000 }),
  generateMaterialPlanWithFile: (formData) => ax.post('/materials/material-plan/generate-with-files', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }),
  listMaterialProjectTypes: () => ax.get('/materials/project-types'),

  // Verktyg & lager
  generateToolsPlan: (data) => ax.post('/inventory/tools-plan/generate', data, { timeout: 120000 }),
  getToolsPlan: (id) => ax.get(`/inventory/tools-plan/${id}`),
  listToolsPlans: (projectId) => ax.get(`/inventory/tools-plan/project/${projectId}/list`),
  toggleToolInStock: (planId, body) => ax.post(`/inventory/tools-plan/${planId}/toggle-stock`, body),
  listInventory: () => ax.get('/inventory/items'),
  createInventoryItem: (data) => ax.post('/inventory/items', data),
  updateInventoryItem: (id, data) => ax.patch(`/inventory/items/${id}`, data),
  deleteInventoryItem: (id) => ax.delete(`/inventory/items/${id}`),

  // Arbete-motor
  generateLaborPlan: (data) => ax.post('/labor/plan/generate', data, { timeout: 180000 }),
  getLaborPlan: (id) => ax.get(`/labor/plan/${id}`),
  listLaborPlans: (projectId) => ax.get(`/labor/plan/project/${projectId}/list`),
  updateLaborPlan: (id, data) => ax.patch(`/labor/plan/${id}`, data),

  // Tid-motor
  generateTimePlan: (data) => ax.post('/time/plan/generate', data, { timeout: 180000 }),
  getTimePlan: (id) => ax.get(`/time/plan/${id}`),
  listTimePlans: (projectId) => ax.get(`/time/plan/project/${projectId}/list`),
  publishTimeSchedule: (planId, publish) => ax.post(`/time/plan/${planId}/publish-schedule`, { publish }),
  getMySchedule: () => ax.get('/time/my-schedule'),
  getSuppliers: () => ax.get('/materials/suppliers'),
  createSupplier: (data) => ax.post('/materials/suppliers', data),
  deleteSupplier: (id) => ax.delete(`/materials/suppliers/${id}`),
  uploadPricelist: (id, formData) => ax.post(`/materials/suppliers/${id}/pricelist`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  compareOffers: (formData) => ax.post('/materials/compare-offers', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  getCompareOfferResults: (projectId) => ax.get(`/materials/compare-offers/project/${projectId}`),

  // AI
  getAISessions: () => ax.get('/chat/sessions'),
  sendAIMessage: (data) => ax.post('/chat/message/send', data, { timeout: 60000 }),
  getAIConversation: (sessionId) => ax.get(`/chat/conversation/${sessionId}`),

  // Support Chat
  getSupportConversation: () => ax.get('/chat/support/my-conversation'),
  sendSupportMessage: (data) => ax.post('/chat/support/send', data),
  getSupportStats: () => ax.get('/chat/support/stats'),

  // AI Cost Estimate
  aiCostEstimate: (data) => ax.post('/projects/ai-cost-estimate', data),

  // Invoice Scanning
  scanInvoice: (formData) => ax.post('/materials/scan-invoice', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getScannedInvoices: (projectId) => ax.get(`/materials/scanned-invoices${projectId ? `?project_id=${projectId}` : ''}`),
  scannedToMaterials: (scanId, planId) => ax.post(`/materials/scanned-invoices/${scanId}/to-materials${planId ? `?plan_id=${planId}` : ''}`),

  // Data Import
  importInvoices: (formData) => ax.post('/import/invoices', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importCustomers: (formData) => ax.post('/import/customers', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Iter48: Universal preview + import (SIE4/Excel/CSV/JSON)
  previewImport: (formData) => ax.post('/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  universalImport: (formData) => ax.post('/import/universal', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importArticles: (formData) => ax.post('/import/articles', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getImportHistory: () => ax.get('/import/history'),
  cleanupEmptyImports: () => ax.post('/import/cleanup-empty'),

  // Sidebar notification badges (röda prickar)
  getNavBadges: () => ax.get('/dashboard/nav-badges'),
  markNavBadgeSeen: (key) => ax.post(`/dashboard/nav-badges/${key}/mark-seen`),

  // Fas 5 (#8): AI-klockning — flaggade utstämplingar
  listFlaggedClockEntries: () => ax.get('/clock/flagged'),
  overtimeApproval: (entryId, data) => ax.post(`/clock/${entryId}/overtime-approval`, data),

  // Project Progress & AI
  updateProjectProgress: (id, data) => ax.put(`/projects/${id}/progress`, data),
  aiTimeEstimate: (id) => ax.post(`/projects/${id}/ai-time-estimate`),
  generateShoppingList: (id, data) => ax.post(`/projects/${id}/generate-shopping-list`, data),
  getShoppingLists: (id) => ax.get(`/projects/${id}/shopping-lists`),

  // Payroll
  calculatePayroll: (params) => ax.get('/payroll/calculate', { params }),
  sendPayslip: (data) => ax.post('/payroll/send-payslip', data),
  // Löneperioder (automatiserad utbetalning: SEPA + AGI + OCR)
  getCurrentMonthPayroll: (params) => ax.get('/payroll/current-month', { params }),
  createPayrollRun: (data) => ax.post('/payroll/runs', data),
  listPayrollRuns: () => ax.get('/payroll/runs'),
  getPayrollRun: (id) => ax.get(`/payroll/runs/${id}`),
  approvePayrollRun: (id) => ax.post(`/payroll/runs/${id}/approve`),
  markPayrollRunPaid: (id) => ax.post(`/payroll/runs/${id}/mark-paid`),
  downloadSepa: (id) => ax.get(`/payroll/runs/${id}/sepa.xml`, { responseType: 'blob' }),
  downloadAgi: (id) => ax.get(`/payroll/runs/${id}/agi.xml`, { responseType: 'blob' }),
  // Worker-lönespec
  listMyPayslips: () => ax.get('/my/payslips'),
  downloadMyPayslip: (id) => ax.get(`/my/payslips/${id}/pdf`, { responseType: 'blob' }),

  // Super Admin
  superadminGetStats: () => ax.get('/superadmin/stats'),
  superadminGetCompanies: () => ax.get('/superadmin/companies'),
  superadminGetCompany: (id) => ax.get(`/superadmin/companies/${id}`),
  superadminGetUsers: () => ax.get('/superadmin/users'),
  superadminToggleCompany: (id, active) => ax.put(`/superadmin/companies/${id}/status?active=${active}`),
  superadminDeleteCompany: (id) => ax.delete(`/superadmin/companies/${id}`),
  superadminCreateUser: (data) => ax.post('/superadmin/create-user', data),
  superadminUpdatePricing: (id, data) => ax.put(`/superadmin/companies/${id}/pricing`, data),
  superadminUpdateFeatures: (id, data) => ax.put(`/superadmin/companies/${id}/features`, data),
  superadminGetSupportAgents: () => ax.get('/superadmin/support-agents'),
  superadminCreateSupportAgent: (data) => ax.post('/superadmin/support-agents', data),
  superadminDeleteSupportAgent: (id) => ax.delete(`/superadmin/support-agents/${id}`),
  superadminGetConversations: () => ax.get('/superadmin/chat/conversations'),
  superadminGetConversationMessages: (id) => ax.get(`/superadmin/chat/conversations/${id}/messages`),
  superadminReplyToConversation: (id, data) => ax.post(`/superadmin/chat/conversations/${id}/reply`, data),
  superadminResolveConversation: (id) => ax.put(`/superadmin/chat/conversations/${id}/resolve`),

  // Internal Chat
  getInternalChats: () => ax.get('/internal-chat/conversations'),
  getInternalChatMessages: (id) => ax.get(`/internal-chat/conversations/${id}/messages`),
  sendInternalMessage: (id, data) => ax.post(`/internal-chat/conversations/${id}/send`, data),
  createInternalChat: (data) => ax.post('/internal-chat/conversations', data),
  getInternalChatColleagues: () => ax.get('/internal-chat/users'),

  // Quick Calc (AI total beräkning)
  quickCalcTotal: (data) => ax.post('/quick-calc/total', data),
  saveQuickCalc: (data) => ax.post('/quick-calc/archive', data),
  listQuickCalcArchive: () => ax.get('/quick-calc/archive'),
  getQuickCalc: (id) => ax.get(`/quick-calc/archive/${id}`),
  deleteQuickCalc: (id) => ax.delete(`/quick-calc/archive/${id}`),
  quickCalcToProject: (id, data) => ax.post(`/quick-calc/archive/${id}/to-project`, data),

  // Personal time (worker private timer, admin can see)
  startPersonalTime: (data) => ax.post('/personal-time/start', data),
  stopPersonalTime: (id, data) => ax.post(`/personal-time/stop/${id}`, data),
  getActivePersonalTime: () => ax.get('/personal-time/active'),
  getPersonalTimeHistory: (days) => ax.get('/personal-time/history', { params: { days } }),
  getPersonalTimeAdminOverview: (params) => ax.get('/personal-time/admin/overview', { params }),

  // Machine service records
  addMachineService: (machineId, data) => ax.post(`/machines/${machineId}/service`, data),
  listMachineService: (machineId) => ax.get(`/machines/${machineId}/service`),
  updateMachineService: (machineId, recordId, data) => ax.put(`/machines/${machineId}/service/${recordId}`, data),
  completeMachineService: (machineId, recordId, data) => ax.post(`/machines/${machineId}/service/${recordId}/complete`, data || {}),
  deleteMachineService: (machineId, recordId) => ax.delete(`/machines/${machineId}/service/${recordId}`),
  upcomingMachineService: () => ax.get('/machines/service/upcoming'),

  // Invoicing (white-label fakturasystem)
  getBillingProfile: () => ax.get('/invoicing/profile'),
  updateBillingProfile: (data) => ax.put('/invoicing/profile', data),
  getBillingCompleteness: () => ax.get('/invoicing/profile/completeness'),
  uploadBillingLogo: (data) => ax.post('/invoicing/profile/logo', { logo_data_url: data }),
  deleteBillingLogo: () => ax.delete('/invoicing/profile/logo'),
  listInvoiceCustomers: () => ax.get('/invoicing/customers'),
  createInvoiceCustomer: (data) => ax.post('/invoicing/customers', data),
  updateInvoiceCustomer: (id, data) => ax.put(`/invoicing/customers/${id}`, data),
  deleteInvoiceCustomer: (id) => ax.delete(`/invoicing/customers/${id}`),
  listInvoices: (status) => ax.get('/invoicing/invoices', { params: status ? { status } : {} }),
  getInvoice: (id) => ax.get(`/invoicing/invoices/${id}`),
  createInvoice: (data) => ax.post('/invoicing/invoices', data),
  updateInvoice: (id, data) => ax.put(`/invoicing/invoices/${id}`, data),
  generateOcr: () => ax.post('/invoicing/generate-ocr'),
  listRecentMaterialNotes: () => ax.get('/materials/material-plan/recent-notes'),
  deleteInvoice: (id) => ax.delete(`/invoicing/invoices/${id}`),
  cancelInvoice: (id) => ax.post(`/invoicing/invoices/${id}/cancel`),
  markInvoicePaid: (id, data) => ax.post(`/invoicing/invoices/${id}/mark-paid`, data || {}),
  signAndSendInvoice: (id, data) => ax.post(`/invoicing/invoices/${id}/sign-and-send`, data),
  // PIN-signering (alternativ till BankID)
  getSigningMethod: () => ax.get('/invoicing/signing-method'),
  setSigningPin: (data) => ax.post('/invoicing/signing-pin', data),
  changeSigningPin: (data) => ax.put('/invoicing/signing-pin', data),
  signInvoiceWithPin: (id, data) => ax.post(`/invoicing/invoices/${id}/sign-with-pin`, data),
  // SuperAdmin — växla signeringsmetod per företag
  saGetCompanySigningMethod: (companyId) => ax.get(`/sa/sales/company/${companyId}/signing-method`),
  saSetCompanySigningMethod: (companyId, data) => ax.post(`/sa/sales/company/${companyId}/signing-method`, data),
  sendInvoiceReminder: (id, stage) => ax.post(`/invoicing/invoices/${id}/send-reminder`, { stage: stage || null }),
  markInvoiceDebtCollection: (id) => ax.post(`/invoicing/invoices/${id}/mark-debt-collection`),
  invoicePdfUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/invoicing/invoices/${id}/pdf`,

  // Dashboard insights
  getDailyBriefing: () => ax.get('/insights/daily-briefing'),
  getRevenueChart: () => ax.get('/insights/revenue'),
  getHealthScore: () => ax.get('/insights/health-score'),
  toggleDailyBriefing: (enabled) => ax.post('/insights/daily-briefing/toggle', { enabled }),

  // Accounting / Bokföring
  getAccountingSettings: () => ax.get('/accounting/settings'),
  updateAccountingSettings: (data) => ax.put('/accounting/settings', data),
  getChartOfAccounts: () => ax.get('/accounting/chart-of-accounts'),
  getJournalEntries: (params) => ax.get('/accounting/journal', { params }),
  createJournalEntry: (data) => ax.post('/accounting/journal', data),
  updateJournalEntry: (id, data) => ax.put(`/accounting/journal/${id}`, data),
  deleteJournalEntry: (id) => ax.delete(`/accounting/journal/${id}`),
  autoBookAll: () => ax.post('/accounting/auto-book-all'),
  getIncomeStatement: (params) => ax.get('/accounting/report/income-statement', { params }),
  getBalanceSheet: (params) => ax.get('/accounting/report/balance-sheet', { params }),
  getAccountingSummary: () => ax.get('/accounting/report/summary'),
  closePeriod: (data) => ax.post('/accounting/close-period', data, { timeout: 60000 }),
  getPeriodCloses: () => ax.get('/accounting/period-closes'),
  // Estimation Engine v2 — deterministisk + spårbar kalkyl
  getProfessionDefaults: () => ax.get('/estimation/profession-defaults'),
  createEstimate: (data) => ax.post('/estimation/estimates', data),
  listEstimates: (projectId) => ax.get('/estimation/estimates', { params: projectId ? { project_id: projectId } : {} }),
  getEstimate: (id) => ax.get(`/estimation/estimates/${id}`),
  deleteEstimate: (id) => ax.delete(`/estimation/estimates/${id}`),

  importAccountingHistory: (formData) => ax.post('/accounting/import-history', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Kvitto-uppladdning (AI-tolkning + bokföring)
  uploadReceipt: (formData) => ax.post('/accounting/upload-receipt', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  listReceipts: (status) => ax.get('/accounting/receipts', { params: status ? { status } : {} }),
  bookReceipt: (id, payload) => ax.post(`/accounting/receipts/${id}/book`, payload || {}),
  deleteReceipt: (id) => ax.delete(`/accounting/receipts/${id}`),
  getReceiptFile: (id) => ax.get(`/accounting/receipts/${id}/file`, { responseType: 'blob' }),

  // Auth (explicit)
  logout: () => ax.post('/auth/logout'),

  // Aliases for backward compatibility
  getMyPinStatus: () => ax.get('/settings/pin'),
  getCurrentConversation: () => ax.get('/chat/support/my-conversation'),
  getCompanyVerificationStatus: () => ax.get('/company/verification-status'),
  sendChatMessage: (data) => ax.post('/chat/message/send', data, { timeout: 60000 }),
  sendInvoiceEmail: (id) => ax.post(`/invoice-drafts/${id}/send`),
  setup2FA: () => ax.post('/auth/2fa/setup'),
  activate2FA: (code) => ax.post(`/auth/2fa/activate?code=${code}`),
  disable2FA: () => ax.post('/auth/2fa/disable'),
  getAITimeEstimate: (id) => ax.post(`/projects/${id}/ai-time-estimate`),
  generateMaterialPlanWithFiles: (formData) => ax.post('/materials/material-plan/generate-with-files', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }),
  createTender: (formData) => ax.post('/tenders', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  compareOffer: (formData) => ax.post('/materials/compare-offers', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  getOfferComparisons: (projectId) => ax.get(`/materials/compare-offers/project/${projectId}`),
  scanReceipt: (formData) => ax.post('/materials/scan-invoice', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getSupplierProducts: (id) => ax.get(`/materials/suppliers/${id}/products`),
  getAllConversations: () => ax.get('/chat/conversations'),
  getConversationMessages: (id) => ax.get(`/chat/conversation/${id}`),
  assignConversation: (id, data) => ax.post(`/chat/support/assign/${id}`, data),
  updateConversationStatus: (id, status) => ax.put(`/chat/support/status/${id}?status=${status}`),
  createCheckoutSession: (data) => ax.post('/billing/create-checkout', data),
  getCheckoutStatus: (sessionId) => ax.get(`/billing/checkout-status/${sessionId}`),
  createSubscription: (data) => ax.post('/billing/subscribe', data),

  // Public Contact + Chat (no auth required)
  getSupportHours: () => axios.get(API_URL + '/contact/support-hours'),
  submitContactForm: (data) => axios.post(API_URL + '/contact/submit', data),
  publicChatStart: (data) => axios.post(API_URL + '/contact/public-chat/start', data, { timeout: 60000 }),
  publicChatSend: (data) => axios.post(API_URL + '/contact/public-chat/send', data, { timeout: 60000 }),
  publicChatMessages: (sessionId) => axios.get(API_URL + `/contact/public-chat/messages/${sessionId}`),
  publicChatRequestHuman: (data) => axios.post(API_URL + '/contact/public-chat/request-human', data),

  // Superadmin — publika chatt-sessioner
  saPublicChatList: () => ax.get('/contact/public-chat/admin/sessions'),
  saPublicChatMessages: (id) => ax.get(`/contact/public-chat/admin/messages/${id}`),
  saPublicChatReply: (data) => ax.post('/contact/public-chat/admin/reply', data),
  saPublicChatResolve: (id) => ax.post(`/contact/public-chat/admin/resolve/${id}`),

  // Password reset
  forgotPassword: (data) => axios.post(API_URL + '/auth/forgot-password', data),
  validateResetToken: (token) => axios.get(API_URL + '/auth/reset-password/validate', { params: { token } }),
  resetPassword: (data) => axios.post(API_URL + '/auth/reset-password', data),

  // Superadmin Sales
  saListPlans: () => ax.get('/sa/sales/plans'),
  saCreateCustomer: (data) => ax.post('/sa/sales/create-customer', data),
  // Iter48: Prenumeration & fakturering
  saPreviewNextInvoice: (companyId) => ax.get(`/sa/sales/preview-next-invoice/${companyId}`),
  switchBillingToCard: (payment_method_id) => ax.post('/subscription/switch-to-card', { stripe_payment_method_id: payment_method_id }),
  saStartSubscription: (companyId) => ax.post(`/sa/sales/start-subscription/${companyId}`),
  saExtendDemo: (userId, data) => ax.post(`/sa/sales/extend-demo/${userId}`, data),
  saSendResetLink: (userId) => ax.post(`/sa/sales/send-reset-link/${userId}`),
  saResendAgreement: (companyId) => ax.post(`/sa/sales/resend-agreement/${companyId}`),
  saStatsOverview: () => ax.get('/sa/stats/overview'),
  saUnlockCompany: (companyId, data) => ax.post(`/sa/sales/unlock-company/${companyId}`, data),
  saCompanyBilling: (companyId) => ax.get(`/sa/sales/company-billing/${companyId}`),
  // Fas 4 (#11): Manuella fakturor
  saSendManualInvoice: (companyId, data) => ax.post(`/sa/sales/send-manual-invoice/${companyId}`, data),
  saListManualInvoices: (companyId) => ax.get(`/sa/sales/manual-invoices/${companyId}`),
  saMarkManualInvoicePaid: (invoiceId) => ax.post(`/sa/sales/manual-invoices/${invoiceId}/mark-paid`),
  saDownloadManualInvoicePdf: (invoiceId) => `${API_URL}/sa/sales/manual-invoices/${invoiceId}/pdf`,
  saProcessScheduledInvoices: () => ax.post('/sa/sales/process-scheduled-invoices'),
  saMarkAutoInvoicePaid: (companyId) => ax.post(`/sa/sales/mark-invoice-paid/${companyId}`),
  postActivationCheckout: (originUrl, coupon) => ax.post('/billing/post-activation-checkout', null, { params: { origin_url: originUrl, ...(coupon ? { coupon } : {}) } }),
  mySubscription: () => ax.get('/billing/my-subscription'),

  // Paywall (betalvägg efter 14 dagar utan aktiv prenumeration)
  paymentRequired: () => ax.get('/billing/payment-required'),
  payNowCheckout: (originUrl) => ax.post('/billing/pay-now-checkout', null, { params: { origin_url: originUrl } }),
  sendInvoiceNow: () => ax.post('/billing/send-invoice-now'),

  // Operio Sales portal
  salesStats: () => ax.get('/sales/stats'),
  salesMyCustomers: () => ax.get('/sales/customers'),
  salesCreateCustomer: (data) => ax.post('/sales/create-customer', data),
  salesCustomerNotes: (companyId) => ax.get(`/sales/customers/${companyId}/notes`),
  salesAddCustomerNote: (companyId, text) => ax.post(`/sales/customers/${companyId}/notes`, { text }),
  // Sales team management (superadmin)
  saListSalesTeam: () => ax.get('/sa/sales-team'),
  saCreateSalesUser: (data) => ax.post('/sa/sales-team', data),
  saDeleteSalesUser: (userId) => ax.delete(`/sa/sales-team/${userId}`),

  // Multi billing profiles
  listBillingProfiles: () => ax.get('/billing-profiles'),
  createBillingProfile: (data) => ax.post('/billing-profiles', data),
  updateBillingProfile: (id, data) => ax.put(`/billing-profiles/${id}`, data),
  deleteBillingProfile: (id) => ax.delete(`/billing-profiles/${id}`),
  setDefaultBillingProfile: (id) => ax.post(`/billing-profiles/${id}/default`),

  // Shifts (worker schedule)
  listShifts: (params) => ax.get('/shifts', { params }),
  listMyShifts: (params) => ax.get('/shifts/mine', { params }),
  createShift: (data) => ax.post('/shifts', data),
  createShiftsBulk: (data) => ax.post('/shifts/bulk', data),
  updateShift: (id, data) => ax.put(`/shifts/${id}`, data),
  deleteShift: (id) => ax.delete(`/shifts/${id}`),

  // Project archive + machines
  archiveProject: (id, archived) => ax.put(`/projects/${id}/archive?archived=${archived}`),
  setProjectMachines: (id, machineIds) => ax.put(`/projects/${id}/machines`, { machine_ids: machineIds || [] }),

  // Customer register migration
  migrateImportedCustomers: () => ax.post('/import/migrate-customers-to-register'),
  dedupeImportedCustomers: () => ax.post('/import/dedupe-customers'),
  // Iter49: Städa gamla labor-rapporter som skapats från maskinstämplingar
  cleanupMachineLinkedTimeReports: () => ax.post('/time-reports/cleanup-machine-linked'),
  countMachineLinkedTimeReports: () => ax.get('/time-reports/machine-linked-count'),
  // Iter50: Native push notifications (FCM)
  registerFcmToken: (data) => ax.post('/push/register-fcm-token', data),
  unregisterFcmToken: (token) => ax.delete(`/push/fcm-token?token=${encodeURIComponent(token)}`),
  testFcmPush: () => ax.post('/push/test-fcm'),

  // Push notifications
  getVapidPublicKey: () => axios.get(API_URL + '/push/vapid-public-key'),
  pushSubscribe: (data) => ax.post('/push/subscribe', data),
  pushUnsubscribe: (data) => ax.post('/push/unsubscribe', data),
  pushTest: () => ax.post('/push/test'),

  // IDURA BankID Signing
  signingStatus: () => ax.get('/signing/status'),
  startEtaSigning: (etaId, data) => ax.post(`/signing/eta/${etaId}/start`, data),
  getSigningSession: (id) => ax.get(`/signing/sessions/${id}`),
  getSignedPdf: (id) => ax.get(`/signing/sessions/${id}/pdf`),
};
